import { useTranslation } from 'react-i18next'
import { find, propEq } from 'ramda'
import moment from 'moment'
import { colors } from '@/styles'
import formatName from '@/utils/format-name'
import CurrencyFormatter from '@/components/currency-formatter'
import getPriceDateOptions from '@/utils/price-date-options'
import {
  ContentWrapper,
  AreaTitle,
  StyledPanel,
  StyledRow,
  StyledLabel,
  StyledValue
} from './elements'

const TransactionSummary = ({
  paymentData: {
    payment_status,
    additional_fee,
    charged,
    description,
    pad_agreement,
    requested_amount,
    transaction_number,
    user,
    tenant,
    completed_at,
    payment_method
  }
}) => {
  const { t } = useTranslation()
  const isUpcoming = payment_status === 'upcoming'
  const paymentStates = {
    collecting_funds: t('b.payment.view.payment_status_pending.text', 'Pending'),
    remitting_funds: t('b.payment.view.payment_status_pending.text', 'Pending'),
    fail: t('b.payment.view.failed.text', 'Failed'),
    upcoming: t('b.payment.view.upcoming.text', 'Upcoming'),
    success: t('b.payment.view.successful.text', 'Successful')
  }

  const paymentColors = {
    success: colors.green48,
    fail: '#FB554B'
  }
  const { [payment_status]: statusColor = colors.regular } = paymentColors

  const firstPaymentDate = pad_agreement?.first_payment_on
  const priceDateOptions = getPriceDateOptions(t)
  const formattedDate = `price_date_${moment(firstPaymentDate).date()}`
  const selectedDateOption = find(propEq('value', formattedDate), priceDateOptions) || {
    label: ''
  }
  const processedDate = completed_at ? moment(completed_at, 'YYYY-MM-DD').format('MM-DD-YYYY') : '-'

  const accountNumber = payment_method?.financial_info?.account_number
  const paymentMethodText = accountNumber ? `PAD - ${accountNumber}` : 'PAD'
  return (
    <ContentWrapper>
      <AreaTitle>{t('b.payment.view.transaction_summary.title', 'Transaction Summary')}</AreaTitle>
      <StyledPanel hSpace={24} vSpace={24}>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.status.label', 'Status')}</StyledLabel>
          <StyledValue color={statusColor}>{paymentStates[payment_status]}</StyledValue>
        </StyledRow>

        {!isUpcoming && (
          <>
            <StyledRow bottom={20}>
              <StyledLabel>
                {t('b.payment.view.confirmation_no.label', 'Confirmation No.')}
              </StyledLabel>
              <StyledValue>{transaction_number ?? '-'}</StyledValue>
            </StyledRow>
            <StyledRow bottom={20}>
              <StyledLabel>{t('b.payment.view.sent_by.label', 'Sent By')}</StyledLabel>
              <StyledValue>{formatName(user)}</StyledValue>
            </StyledRow>
            <StyledRow bottom={20}>
              <StyledLabel>{t('b.payment.view.to.label', 'To')}</StyledLabel>
              <StyledValue>{formatName(tenant)}</StyledValue>
            </StyledRow>
          </>
        )}
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.detail.label', 'Detail')}</StyledLabel>
          <StyledValue>
            <StyledValue>{description}</StyledValue>
          </StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.payment_method.label', 'Payment Method')}</StyledLabel>
          <StyledValue>
            <StyledValue>{paymentMethodText}</StyledValue>
          </StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>
            {t('b.payment.view.payment_frequency.label', 'Payment Frequency')}
          </StyledLabel>
          <StyledValue>
            <StyledValue>{t('b.payment.view.monthly.label', 'Monthly')}</StyledValue>
          </StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.payment_date.label', 'Payment Date')}</StyledLabel>
          <StyledValue>{selectedDateOption?.label}</StyledValue>
        </StyledRow>
        {!isUpcoming && (
          <StyledRow bottom={20}>
            <StyledLabel>
              {t('b.payment.view.payment_processed_date.label', 'Payment Processed Date')}
            </StyledLabel>
            <StyledValue>{processedDate}</StyledValue>
          </StyledRow>
        )}
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.rent_price.label', 'Rent Price')}</StyledLabel>
          <StyledValue>
            <CurrencyFormatter value={requested_amount} showCents />
          </StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.additional_fees.label', 'Additional Fees')}</StyledLabel>
          <StyledValue>
            <CurrencyFormatter value={additional_fee} showCents />
          </StyledValue>
        </StyledRow>
        <StyledRow bottom="0" paddingTop={30} borderTop>
          <StyledLabel bottom="0" bold>
            {t('b.payment.view.make_payment_total.label', 'Total')}
          </StyledLabel>
          <StyledValue bottom="0">
            <CurrencyFormatter value={charged} showCents />
          </StyledValue>
        </StyledRow>
      </StyledPanel>
    </ContentWrapper>
  )
}

export default TransactionSummary
